import type { SEOData, LighthouseScores } from '../types';

// Business-Outreach API client. Mirrors leadGenApi.ts in organisation, but keeps its OWN fetch
// helper (not the shared `api` client) on purpose: outreach endpoints throw the raw FastAPI body
// and several flows depend on that shape (e.g. the 409 `requires_confirmation` phase-change), and
// they must NOT trigger the shared client's 401-redirect. Behaviour is identical to the previous
// inline slice helper · this only relocates it.

const API = '/api/outreach';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { ...init, headers: { ...authHeaders(), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw err;
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Types (API request/response shapes · re-exported from the slice for consumers)
// ---------------------------------------------------------------------------

export interface TargetPlatform {
  id: number;
  platform: string;
  current_phase: 'kalt' | 'interesse' | 'vertiefung' | 'closing';
  style: 'casual' | 'professional' | 'direkt' | 'empathisch';
  added_at: string;
  /**
   * Der Beziehungszustand — abgeleitet auf dem SERVER (db_web/conversation_states.py).
   * Der Browser vergleicht nur, er rechnet nicht mit: fuenf eigene Umsetzungen derselben
   * Stufenlogik sind hier schon einmal auseinandergelaufen (Commit eadb5256).
   */
  state?: {
    state: string;
    sub_state: string | null;
    marker: string | null;
    source: 'derived' | 'override';
    reason: string;
  };
}

export interface IGEnrichmentSnapshot {
  id: number;
  bio: string | null;
  followers: number | null;
  following: number | null;
  posts_count: number | null;
  has_bio_link: number | null;
  account_type: string | null;
  engagement_hint: string | null;
  posting_style: string | null;
  pain_point_hint: string | null;
  summary_line: string | null;
  created_at: string;
}

export interface WebsiteEnrichmentSnapshot {
  id: number;
  method: string;
  url: string | null;
  summary: string | null;
  notes: string | null;
  created_at: string;
}

export interface MapsCompetitor {
  name: string;
  rating: number | null;
  reviews: number | null;
  address: string | null;
}

export interface OutreachTarget {
  id: number;
  user_id: number;
  company_name: string;
  industry: string | null;
  pain_point: string | null;
  contact_name: string | null;
  competitor: string | null;
  platform: string;
  language: string;
  current_phase: string;
  notes: string | null;
  created_at: string;
  archived_at: string | null;
  // v2 fields
  platforms: TargetPlatform[];
  ig_username: string | null;
  website_url: string | null;
  website_summary: string | null;
  strategy_id: number | null;
  maps_data: string | null;
  maps_analyzed_at: string | null;
  latest_ig_enrichment: IGEnrichmentSnapshot | null;
  // contact + social fields
  phone: string | null;
  whatsapp: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  tiktok_url: string | null;
  linkedin_url: string | null;
  // hierarchy fields
  entry_type: 'company' | 'contact';
  parent_id: number | null;
  contacts: OutreachTarget[];
  // SEO Intelligence fields
  seo_organic_traffic: number | null
  seo_keyword_count: number | null
  seo_backlinks_count: number | null
  seo_referring_domains: number | null
  seo_no_data: boolean
  seo_analyzed_at: string | null
  seo_lighthouse_json: string | null
  seo_cost_usd: number | null
  // Bio-Link Resolution
  bio_link_url: string | null
  resolved_domain: string | null
  all_bio_links_json: string | null
  // Deal Outcome (ICP)
  deal_outcome: 'won' | 'lost' | 'pending' | null
  deal_value: number | null
  is_customer: boolean
  // Contact email + client link + Fraud0 lead handoff
  email: string | null
  client_id: number | null
  fraud0_lead_id: number | null
  fraud0_pitch_json: string | null
  marketing_contact_name: string | null
  marketing_linkedin_url: string | null
  marketing_contact_status: string | null
  marketing_position: string | null
  marketing_contact_offered_at: string | null
  marketing_contact_offered_by: number | null
}

// ── Fraud0-pitch message generator types ────────────────────────────────────
export interface OutreachLayer { key: string; label: string; enabled: boolean; size: string; prompt: string }
export interface OutreachTag { label: string; prompt: string; prefers?: string[]; enabled?: boolean }
export interface PitchTemplateSection { key: string; label: string; content: string }
export interface PitchTemplate { key: string; name: string; anrede: string; sections: PitchTemplateSection[] }
export interface PitchConfig {
  layers: OutreachLayer[];
  tags: Record<string, OutreachTag>;
  defaults: { anrede: string; cta_style: string; length_chars: number; sender_name: string; greeting: string; fr_offerer: string };
  template: PitchTemplate;
}
export interface Offerer { id: number; name: string }
export interface SectionSnippet { key: string; label: string; tag: string; text: string }
export interface DefaultMessage {
  template_key: string;
  template_name: string;
  sections: Record<string, string>;
  order: string[];
  labels: Record<string, string>;
  anrede: string;
  unresolved: string[];
  snippets?: Record<string, SectionSnippet[]>;
}
export interface HookOption { cache_id?: number; hook_id?: string; filled_text: string; reason?: string; tag?: string | null; used?: boolean }
export interface MessageVariant { tag: string | null; sections: Record<string, string>; text: string; warnings: string[]; order?: string[] }
export interface MessageTemplate { id: number; name: string; platform?: string; tag?: string; anrede?: string; sections: Record<string, string>; sizes?: Record<string, string> | null }

// ── Geteilte Textbaustein-Bibliothek ────────────────────────────────────────
export interface LibraryText {
  id: number;
  root_id: number | null;
  parent_id: number | null;
  is_main: number;
  version: number;
  kind: 'message' | 'snippet';
  name: string;
  channel: string;
  phase: string | null;
  round_no: number | null;
  language: string;
  body: string;
  subject: string | null;
  status: 'draft' | 'published' | 'archived';
  owner_id: number;
  condition: Record<string, unknown> | null;
  sections: Record<string, string> | null;
  sent_count: number;
  reply_count: number;
  review_count: number;
  positive_count: number;
  wilson_score: number;
  updated_at: string;
  // Die Tresor-Einordnung (S2/S6a/S6b). Der Server liefert sie seit jeher mit (`SELECT *`), der
  // Typ hat sie nur verschwiegen. Sie ist NICHT über die API schreibbar — `_LIBRARY_EDITABLE` in
  // db_web/outreach.py lässt bewusst nur die Formulierung durch, nicht die Einordnung.
  situation_type?: string | null;
  situation_label?: string | null;
  block_type?: string | null;
  /** 'roh' = importierter Tresor-Wortlaut, 'adaptiert' = B2B-Fassung aus S6b. */
  adapt_status?: string | null;
  is_sendable?: number;
  /** Regieanweisung des Tresors — ein HINWEIS an den Menschen, nie Teil des Textes. */
  coaching_note?: string | null;
  /** Woher der Text stammt. 'mail_bausteine' = aus dem Mail-Pult eingestellt; er traegt dann
   *  {platzhalter} samt bedingter Bloecke und muss durch renderEmailTpl, NICHT durch den
   *  {{token}}-Renderer der Bibliothek — sonst ginge „{firma}" roh in der Mail raus. */
  origin?: string | null;
}
export interface LibraryFamily { root_id: number; main: LibraryText; forks: LibraryText[] }

// ── Outreach-CRM: die Arbeitsliste ──────────────────────────────────────────
export interface CrmPerson {
  person_key: string;
  person_name: string | null;
  position: string | null;
  linkedin_url: string | null;
  /** Vernetzungsstand aus fraud0: 'pending' | 'connected' | 'not_offered' | 'rejected' | null.
   *  Wird hier nur GELESEN; geschrieben wird er in fraud0, wo die FR-Tagessperre hängt. */
  fr_status: string | null;
  person_email: string | null;
  person_phone: string | null;
  /** Alles ab hier ist NULL, solange die Person in keiner Sequenz steht. */
  stand_id: number | null;
  sequenz_id: number | null;
  sequenz_name: string | null;
  schritt_id: number | null;
  schritt_name: string | null;
  schritt_nr: number | null;
  schritt_text: string | null;
  schritt_kanal: string | null;
  /** Grund, unter dem dieser Schritt übersprungen werden kann („kein Beitrag", „bereits vernetzt"). */
  schritt_ueberspring_grund: string | null;
  /** Worauf der Schritt wartet: 'tage' | 'annahme' | 'antwort'. */
  schritt_warten_auf: string | null;
  schritt_spaetestens_tage: number | null;
  /** 1 = dieser Schritt wiederholt sich, bis ein Ergebnis gesetzt wird. */
  schritt_schleife: number | null;
  /** Wie oft dieser Schritt schon rausging — die Rundenzahl der Schleife. */
  runde: number | null;
  /** Zeitpunkt der Mail-Einwilligung („schick mir eine Mail"). NULL = keine Mail erlaubt. */
  mail_erlaubt_at: string | null;
  /** Wann sich am Stand zuletzt etwas getan hat — zeigt, wie lange schon gewartet wird. */
  stand_geaendert_at: string | null;
  faellig_at: string | null;
  zustand: string | null;
  notiz: string | null;
  besitzer_id: number | null;
  /** 0 = heute fällig · 1 = neu · 2 = wartet · 3 = erledigt. Bestimmt die Reihenfolge. */
  eimer: number;
  /** Dateiname des zwischengespeicherten Profilfotos, null wenn keins da ist. */
  bild: string | null;
}
export interface CrmFirma {
  lead_id: number;
  company_name: string;
  domain: string;
  lead_status: string;
  branche: string | null;
  /** Rückholbarer Betrag aus der bezahlten Suche und Anteil bezahlten Traffics. Beide
   *  bestimmen die Reihenfolge der Liste: oben steht, wo am meisten zu holen ist. */
  recoverable_eur: number | null;
  paid_share: number | null;
  /** Dateiname des zwischengespeicherten Firmenlogos. */
  bild: string | null;
  /** Logo ohne eigene Flaeche (z. B. adidas, bonprix) — bekommt im dunklen Theme eine weisse. */
  bild_durchsichtig?: boolean;
  /** Die persoenliche Fraud-Seite dieser Firma, sofern angelegt. */
  seite?: CrmSeite | null;
  personen: CrmPerson[];
}

/** Eine vorbereitete persoenliche Seite (leitkraft.de/fraud-detector/<slug>). */
export interface CrmSeite {
  slug: string;
  lead_id: number;
  url: string;
  /** Erst wenn die Seite unter ihrer Adresse antwortet, darf der Link in eine Nachricht. */
  veroeffentlicht: boolean;
  erstellt_at: string;
  /** Wann der Empfaenger sie zum ERSTEN Mal geoeffnet hat. Null = noch nie. */
  zuerst_geoeffnet_at?: string | null;
  /** Wie oft insgesamt. Dreimal geoeffnet heisst etwas anderes als einmal. */
  aufrufe?: number;
  eintrag: {
    modus?: 'hochrechnung' | 'offen';
    /** An wen die Seite geht · entscheidet, wie sie sich liest (Marketing vs. Geschäftsführung). */
    adressat?: 'marketing' | 'gf';
    monthlyAdSpend?: number;
    botRate?: number;
    wastedEur?: number;
    cpc?: number;
    [k: string]: unknown;
  };
}

// ── Sequenzen: eine Gruppe, darin geordnete Schritte ─────────────────────────
export interface SequenzSchritt {
  id: number;
  sequenz_id: number;
  /** Platz in der Reihe, ab 1 und lückenlos. Der Server vergibt sie neu, sobald sich die
   *  Reihenfolge ändert — merken darf man sich nur die id. */
  position: number;
  name: string;
  text: string;
  notiz: string | null;
  kanal: string;
  /** Tage Abstand zum vorigen Schritt. 0 = am selben Tag. */
  wartetage: number;
  /** Beschriftung des „nicht möglich"-Knopfes im CRM, z. B. „bereits vernetzt". */
  ueberspring_grund?: string | null;
  /** 'tage' = fällig nach `wartetage`. 'annahme'/'antwort' = erst fällig, wenn die Anfrage
   *  angenommen wurde bzw. die Person geantwortet hat. */
  warten_auf?: 'tage' | 'annahme' | 'antwort' | 'seite';
  /** Notbremse: bleibt das Ereignis aus, wird der Schritt nach so vielen Tagen trotzdem fällig. */
  spaetestens_tage?: number | null;
  /** 1 = Schleife: der Schritt wiederholt sich, bis ein Ergebnis gesetzt wird. */
  schleife?: number;
}
export interface Sequenz {
  id: number;
  name: string;
  beschreibung: string | null;
  kanal: string;
  besitzer_id: number | null;
  besitzer_name: string | null;
  archiviert_at: string | null;
  erstellt_at: string;
  /** Nur in der Liste gesetzt; die Einzelansicht liefert `schritte`. */
  schritte_anzahl?: number;
  schritte?: SequenzSchritt[];
}
/** Zählung je Situation für die Sichtung — kommt aus GET /library/overview. */
export interface SituationOverview { situation_type: string; draft: number; published: number }

/** Ein Baustein als REGEL: Feldpfad + Formatvorschrift. Werte rechnet der Client daraus selbst,
 *  damit der fraud0-Wisch-Stapel keine Anfrage je Karte braucht. */
export interface TokenSpec {
  key: string; label: string; group: string; format: string;
  hint?: string; source?: string[]; derived?: string;
  min_display?: number; limit?: number; only?: string[];
  snippet_id?: number; condition?: Record<string, unknown> | null;
  /** Nur Bausteine: Text und die bereits zu Zahlen aufgeloesten Schwellen. Ohne beides koennte
   *  der Browser den Baustein weder einsetzen noch seine Bedingung pruefen. */
  body?: string;
}
export interface TokenGroup { key: string; label: string }

export interface OutreachPoolText {
  id: number;
  source_id: number;
  content: string;
  phase: string;
  category: string | null;
  platform: string;
  language: string;
  wilson_score: number;
  use_count: number;
  review_count: number;
  positive_count: number;
  is_active: number;
  is_deranked: number;
  is_experimental?: number;
  business_adaptability?: number;
  /** 'manual' = von Hand angelegte Fassung. Leer/NULL = maschinell erzeugt. */
  origin?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  /** Die Variante, aus der diese abgezweigt wurde. NULL = direkt aus dem Quelltext. */
  parent_pool_id?: number | null;
  /** Beziehungszustand (S1). `phase` bleibt daneben stehen und wird nicht umgeschrieben. */
  state?: string | null;
  sub_state?: string | null;
  /** Der Fragetyp innerhalb eines Unterfalls — die dritte Ebene beim Blaettern. */
  sub_case?: string | null;
}

/** Die drei Stufen hinter einer Variante. `original` ist NULL bei frisch erzeugten Texten. */
export interface TextLineage {
  pool_text_id: number;
  variant: string;
  source: string;
  source_id: number;
  original: string | null;
  original_path: string | null;
  is_generated: boolean;
  platform: string;
  language: string;
  phase: string;
  category: string | null;
  subcategory: string | null;
  origin: string | null;
  parent_pool_id: number | null;
}

export interface OutreachSentMessage {
  id: number;
  user_id: number;
  target_id: number;
  pool_text_id: number;
  phase: string;
  personalized_text: string | null;
  actual_sent_text: string | null;
  sent_at: string;
  review_due_at: string | null;
  reviewed_at: string | null;
  company_name?: string;
  platform?: string;
}

export interface FunnelStats {
  kalt: number;
  interesse: number;
  vertiefung: number;
  closing: number;
}

// LinkedIn FR tracking ------------------------------------------------------
export interface ConnectionAccountStat {
  id: number;
  name: string;
  today_count: number;
  total_offered: number;
  total_accepted: number;
  accept_rate: number | null;
}
export interface ConnectionStats { daily_limit: number; accounts: ConnectionAccountStat[] }
export interface FrEvent {
  account_id: number | null;
  event_type: 'offered' | 'accepted' | 'reset';
  source_table: string | null;
  source_id: number | null;
  slot: string | null;
  occurred_at: string;
}
export interface FrBlocked { account_id: number | null; name: string | null; occurred_at: string; note: string | null }
export interface FrLatency { account_id: number | null; hours: number }
export interface FrTracking {
  daily_limit: number;
  accounts: ConnectionAccountStat[];
  events: FrEvent[];
  blocked: FrBlocked[];
  acceptance_latency: FrLatency[];
}

export interface CopyResult { personalized_text: string; pool_text_id: number; phase: string; platform: string }
export interface PhaseChangeBody { phase: string; confirm_skip: boolean; platform?: string }
export interface GenerateMessageBody { tags: (string | null)[]; anrede: string; cta_style?: string; length_chars?: number; hooks?: Record<string, string> }
export interface RewriteSectionBody { section_key: string; sections: Record<string, string>; tag: string | null; instruction?: string; anrede?: string }
export interface SavePitchConfigBody { kind: 'layer' | 'tag' | 'default' | 'template_section'; key: string; prompt_text?: string; enabled?: boolean; size?: string; strategy_id?: number | null }
export interface SaveTemplateBody { name: string; sections: Record<string, string>; tag?: string; anrede?: string; sizes?: Record<string, string> }

// ---------------------------------------------------------------------------
// Endpoints (one method per route; thin typed wrappers, response shapes unchanged)
// ---------------------------------------------------------------------------

export const outreachApi = {
  // Targets ------------------------------------------------------------------
  listTargets: (platform?: string, industry?: string) => {
    const p = new URLSearchParams();
    if (platform) p.set('platform', platform);
    if (industry) p.set('industry', industry);
    return apiFetch<OutreachTarget[]>(`${API}/targets?${p}`);
  },
  getTarget: (id: number) => apiFetch<OutreachTarget>(`${API}/targets/${id}`),
  createTarget: (data: Record<string, unknown>) =>
    apiFetch<OutreachTarget>(`${API}/targets`, { method: 'POST', body: JSON.stringify(data) }),
  updateTarget: (id: number, data: Record<string, unknown>) =>
    apiFetch<OutreachTarget>(`${API}/targets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  archiveTarget: (id: number) => apiFetch<void>(`${API}/targets/${id}`, { method: 'DELETE' }),
  changePhase: (id: number, body: PhaseChangeBody) =>
    apiFetch<OutreachTarget>(`${API}/targets/${id}/phase`, { method: 'PUT', body: JSON.stringify(body) }),
  similarTargets: (industry?: string, competitor?: string) => {
    const p = new URLSearchParams();
    if (industry) p.set('industry', industry);
    if (competitor) p.set('competitor', competitor);
    return apiFetch<OutreachTarget[]>(`${API}/targets/similar?${p}`);
  },

  // Platforms ----------------------------------------------------------------
  addPlatform: (targetId: number, platform: string, style?: string) =>
    apiFetch<TargetPlatform>(`${API}/targets/${targetId}/platforms`, {
      method: 'POST', body: JSON.stringify({ platform, style: style || 'professional' }),
    }),
  removePlatform: (targetId: number, platform: string) =>
    apiFetch<void>(`${API}/targets/${targetId}/platforms/${platform}`, { method: 'DELETE' }),
  updatePlatform: (targetId: number, platform: string, data: Partial<TargetPlatform>) =>
    apiFetch<void>(`${API}/targets/${targetId}/platforms/${platform}`, {
      method: 'PUT', body: JSON.stringify(data),
    }),

  // Pool texts + sending -----------------------------------------------------
  // `generate` kostet Geld: der Server laesst dann Sonnet die fehlenden Pool-Varianten schreiben.
  // Ohne das Flag liefert er nur, was schon da ist, plus die Zahl der fehlenden Quellen.
  browse: (phase: string, platform: string, language: string, generate = false) =>
    apiFetch<{ texts: OutreachPoolText[]; missing_sources?: number }>(
      `${API}/browse/${phase}?${new URLSearchParams(
        generate ? { platform, language, generate: '1' } : { platform, language },
      )}`,
    ),
  /**
   * Texte eines BEZIEHUNGSZUSTANDS statt einer Trichterphase.
   *
   * Bewusst OHNE `generate`-Flag: der Server bietet es auf dieser Route gar nicht an. Ein neuer
   * Chip waere sonst der bequemste Weg, die frueher bei jedem Blaettern laufende Nachgenerierung
   * (drei Sonnet-Rewrites je fehlender Quelle) zurueckzuholen.
   */
  browseState: (state: string, platform: string, language: string, subState?: string) =>
    apiFetch<{
      texts: OutreachPoolText[];
      state: string;
      sub_state: string | null;
      phase: string;
      connectors: OutreachPoolText[];
    }>(
      `${API}/browse/state/${state}?${new URLSearchParams(
        subState ? { platform, language, sub_state: subState } : { platform, language },
      )}`,
    ),

  /**
   * Den Beziehungszustand von Hand setzen, oder mit `state: null` zuruecknehmen.
   *
   * Wirft bei einem ungewoehnlichen Uebergang 409 mit `{requires_confirmation, message}`. Der
   * Aufrufer MUSS das fangen: der frueher hier stehende `changePhase`-Pfad hatte kein `catch`,
   * hat den 409 verschluckt, und ein Rueckwaertsklick tat schlicht nichts.
   */
  setState: (targetId: number, body: {
    state: string | null; platform: string; sub_state?: string | null; confirmed?: boolean;
  }) => apiFetch<OutreachTarget>(`${API}/targets/${targetId}/state`, {
    method: 'PUT', body: JSON.stringify(body),
  }),

  /**
   * Einen Merker setzen oder mit `marker: null` wegnehmen.
   *
   * Keine Rueckfrage und kein 409: ein Merker wirft keinen erreichten Stand weg. Er laesst
   * Zustand und Phase in Ruhe, deshalb gibt es hier nichts zu bestaetigen.
   */
  setMarker: (targetId: number, body: { marker: string | null; platform: string }) =>
    apiFetch<OutreachTarget>(`${API}/targets/${targetId}/marker`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  /** Die Zahlen auf den Zustands-Chips. Reines Zaehlen, kein Spend. */
  stateCounts: (platform: string, language: string) =>
    apiFetch<{
      states: Record<string, number>;
      sub_states: Record<string, number>;
      unassigned: number;
      order: string[];
      sub_order: string[];
    }>(`${API}/states/counts?${new URLSearchParams({ platform, language })}`),

  /** Herkunft einer Variante — bewusst faul geladen, erst beim Aufklappen. */
  textLineage: (poolTextId: number) =>
    apiFetch<TextLineage>(`${API}/pool-texts/${poolTextId}/lineage`),

  /** Eigene Fassung anlegen, abgezweigt von einer Variante oder vom Quelltext. */
  createVariant: (body: {
    content: string;
    from_pool_id?: number;
    from_source_id?: number;
    platform?: string;
    language?: string;
  }) => apiFetch<OutreachPoolText>(`${API}/variants`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  browseExperimental: (phase: string, platform: string, language: string) =>
    apiFetch<{ texts: OutreachPoolText[] }>(`${API}/browse/${phase}/experimental?${new URLSearchParams({ platform, language })}`),
  copy: (body: Record<string, unknown>) =>
    apiFetch<CopyResult>(`${API}/copy`, { method: 'POST', body: JSON.stringify(body) }),

  // ── Geteilte Textbaustein-Bibliothek ──────────────────────────────────────
  // Verwaltet in Business Outreach, benutzt auch von den fraud0-Karten.
  listLibrary: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
    // `count` ist die GESAMTZAHL des Filters, nicht die Länge von `items` — bei gesetztem `limit`
    // sagt `truncated`, dass geschnitten wurde.
    return apiFetch<{ families?: LibraryFamily[]; items?: LibraryText[]; count: number;
                      truncated?: boolean }>(`${API}/library?${q.toString()}`);
  },
  /** Zählung je Situation für die Sichtung, ohne 134 Fließtexte zu laden. */
  libraryOverview: () =>
    apiFetch<{ situations: SituationOverview[] }>(`${API}/library/overview`),
  /** Mehrere Entwürfe auf einmal freigeben (nur Admin). Bewusst über IDs: freigegeben wird nur,
   *  was ein Mensch vorher auf dem Schirm hatte. Idempotent — der zweite Lauf meldet 0. */
  publishLibraryBatch: (ids: number[]) =>
    apiFetch<{ published: number; skipped: number }>(`${API}/library/publish-batch`,
      { method: 'POST', body: JSON.stringify({ ids }) }),
  /** Ein Text samt seiner Familie. In der Sichtung der Weg zum Tresor-Original eines Forks —
   *  faul geladen, nicht je Zeile im Voraus. Für eingeschränkte Rollen ist ein 403 hier der
   *  Normalfall (`/library/{id}` gehört zur Verwaltungsansicht). */
  getLibraryText: (id: number) =>
    apiFetch<{ text: LibraryText; family: LibraryText[] }>(`${API}/library/${id}`),
  createLibraryText: (body: Record<string, unknown>) =>
    apiFetch<{ id: number; text: LibraryText }>(`${API}/library`, { method: 'POST', body: JSON.stringify(body) }),
  updateLibraryText: (id: number, body: Record<string, unknown>) =>
    apiFetch<{ text: LibraryText }>(`${API}/library/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  forkLibraryText: (id: number, body: Record<string, unknown> = {}) =>
    apiFetch<{ id: number; text: LibraryText }>(`${API}/library/${id}/fork`, { method: 'POST', body: JSON.stringify(body) }),
  publishLibraryText: (id: number) =>
    apiFetch<{ text: LibraryText }>(`${API}/library/${id}/publish`, { method: 'POST' }),
  promoteLibraryText: (id: number) =>
    apiFetch<{ text: LibraryText }>(`${API}/library/${id}/promote`, { method: 'POST' }),
  archiveLibraryText: (id: number) =>
    apiFetch<{ text: LibraryText }>(`${API}/library/${id}`, { method: 'DELETE' }),
  /** Ausgewählt/eingefügt — noch nicht gesendet. Der Sende-Zähler läuft ausschließlich über die
   *  fraud0-Sendepfade bzw. POST /api/outreach/sent. */
  markLibraryUsed: (id: number) =>
    apiFetch<{ ok: boolean }>(`${API}/library/${id}/use`, { method: 'POST' }),
  renderLibraryText: (id: number, body: Record<string, unknown>) =>
    apiFetch<{ body: string; subject: string | null; values: Record<string, string> }>(
      `${API}/library/${id}/render`, { method: 'POST', body: JSON.stringify(body) }),
  // Die REGELN, nicht die Werte — einmal pro Sitzung geholt (s. libraryCatalogCache.ts).
  libraryCatalog: () =>
    apiFetch<{ groups: TokenGroup[]; tokens: TokenSpec[] }>(`${API}/library/catalog`),
  markSent: (body: Record<string, unknown>) =>
    apiFetch<{ sent_message_id: number }>(`${API}/sent`, { method: 'POST', body: JSON.stringify(body) }),
  getVariables: (targetId: number) =>
    apiFetch<{ variables: { token: string; label: string; value: string }[] }>(`${API}/targets/${targetId}/variables`),
  submitReview: (body: Record<string, unknown>) =>
    apiFetch<void>(`${API}/review`, { method: 'POST', body: JSON.stringify(body) }),
  undoSent: (id: number) =>
    apiFetch<{ ok: boolean }>(`${API}/sent/${id}/undo`, { method: 'POST' }),

  // Enrichment ---------------------------------------------------------------
  analyzeIG: (targetId: number, screenshot: string) =>
    apiFetch<{ analysis: IGEnrichmentSnapshot }>(`${API}/targets/${targetId}/ig-enrichment`, {
      method: 'POST', body: JSON.stringify({ screenshot }),
    }),
  igHistory: (targetId: number) =>
    apiFetch<{ snapshots: IGEnrichmentSnapshot[] }>(`${API}/targets/${targetId}/ig-enrichment`),
  enrichWebsite: (targetId: number, body: Record<string, unknown>) =>
    apiFetch<void>(`${API}/targets/${targetId}/website-enrichment`, { method: 'POST', body: JSON.stringify(body) }),
  websiteHistory: (targetId: number) =>
    apiFetch<{ snapshots: WebsiteEnrichmentSnapshot[] }>(`${API}/targets/${targetId}/website-enrichment`),
  mapsAnalysis: (targetId: number, query: string) =>
    apiFetch<{ competitors: MapsCompetitor[] }>(`${API}/targets/${targetId}/maps-analysis`, {
      method: 'POST', body: JSON.stringify({ query }),
    }),

  // Stats + reviews ----------------------------------------------------------
  pendingReviews: () =>
    apiFetch<{ reviews: OutreachSentMessage[]; count: number }>(`${API}/pending-reviews`),
  /**
   * Trichter UND Zustaende in EINER Antwort — nebeneinander, nicht anstelle. Beide zaehlen
   * dieselbe Menge Ziele (`get_state_stats`), sonst waeren die Zahlen nicht vergleichbar.
   * `markers` zaehlt separat und ergibt bewusst eine kleinere Summe: ein Merker tritt ZUSAMMEN
   * mit einem Zustand auf, nie an dessen Stelle.
   */
  stats: () => apiFetch<{
    funnel: FunnelStats; pending_reviews: number;
    states: Record<string, number>; markers: Record<string, number>;
    order: string[]; marker_order: string[]; total: number;
  }>(`${API}/stats`),
  deranked: () => apiFetch<OutreachPoolText[]>(`${API}/stats/deranked`),

  // SEO Intelligence ---------------------------------------------------------
  seoBatch: (body: Record<string, unknown>) =>
    apiFetch<void>(`${API}/seo-batch`, { method: 'POST', body: JSON.stringify(body) }),
  seoSingle: (targetId: number) =>
    apiFetch<SEOData>(`${API}/targets/${targetId}/seo-enrichment`, { method: 'POST' }),
  lighthouse: (targetId: number) =>
    apiFetch<LighthouseScores | null>(`${API}/targets/${targetId}/lighthouse`, { method: 'POST' }),
  resolveBioLink: (targetId: number, url: string) =>
    apiFetch<{ resolved_domain: string | null; all_links: string[]; method: string }>(
      `${API}/targets/${targetId}/resolve-bio-link`, { method: 'POST', body: JSON.stringify({ url }) }),
  seoData: (targetId: number) => apiFetch<SEOData>(`${API}/targets/${targetId}/seo-data`),

  // FR offerers (LinkedIn accounts/team members) + this user's resolved default ----------------
  getOfferers: () =>
    apiFetch<{ offerers: Offerer[]; default_offerer: number | null }>(`${API}/offerers`),
  // Fraud0-pitch message generator ------------------------------------------
  getPitchConfig: (strategyId?: number | null) =>
    apiFetch<PitchConfig>(`${API}/pitch-config${strategyId ? `?strategy_id=${strategyId}` : ''}`),
  defaultMessage: (targetId: number) =>
    apiFetch<DefaultMessage>(`${API}/targets/${targetId}/default-message`),
  savePitchConfig: (body: SavePitchConfigBody) =>
    apiFetch<PitchConfig>(`${API}/pitch-config`, { method: 'POST', body: JSON.stringify(body) }),
  suggestHooks: (targetId: number, tag: string | null, anrede: string, forceNew = false) =>
    apiFetch<{ options: HookOption[]; from_cache: boolean; tag: string | null }>(`${API}/suggest-hooks`, {
      method: 'POST', body: JSON.stringify({ target_id: targetId, tag, anrede, force_new: forceNew }),
    }),
  rewriteHook: (targetId: number, original: string, tag: string | null, instruction?: string, anrede = 'sie') =>
    apiFetch<{ filled_text: string }>(`${API}/rewrite-hook`, {
      method: 'POST', body: JSON.stringify({ target_id: targetId, original, tag, instruction, anrede }),
    }),
  generateMessage: (targetId: number, body: GenerateMessageBody) =>
    apiFetch<{ variants: MessageVariant[] }>(`${API}/generate-message`, {
      method: 'POST', body: JSON.stringify({ target_id: targetId, ...body }),
    }),
  rewriteSection: (targetId: number, body: RewriteSectionBody) =>
    apiFetch<{ section_key: string; content: string; warnings: string[] }>(`${API}/rewrite-section`, {
      method: 'POST', body: JSON.stringify({ target_id: targetId, ...body }),
    }),
  listTemplates: () => apiFetch<{ items: MessageTemplate[] }>(`${API}/message-templates`),
  saveTemplate: (body: SaveTemplateBody) =>
    apiFetch<{ id: number }>(`${API}/message-templates`, { method: 'POST', body: JSON.stringify(body) }),

  // ── Outreach-CRM ──────────────────────────────────────────────────────────
  // Die Liste ist eine Abfrage ueber fraud0-Leads, kein eigener Bestand — es gibt bewusst
  // kein "importieren". Jede Aktion spricht eine Person ueber lead_id + person_key an.
  crmListe: (bereich = 'faellig', suche?: string) => {
    const q = new URLSearchParams({ bereich });
    if (suche) q.set('suche', suche);
    return apiFetch<{ firmen: CrmFirma[]; firmen_gesamt: number;
                      zaehlungen: Record<string, number>; zustaende: string[] }>(
      `${API}/crm?${q.toString()}`);
  },
  crmStart: (leadId: number, personKey: string, sequenzId: number) =>
    apiFetch<{ stand: unknown }>(`${API}/crm/start`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey, sequenz_id: sequenzId }),
    }),
  crmSchrittErledigt: (leadId: number, personKey: string) =>
    apiFetch<{ stand: unknown }>(`${API}/crm/schritt-erledigt`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey }),
    }),
  /** Schritt war NICHT MOEGLICH (kein Beitrag zum Liken o. ae.). Zaehlt nicht als Versand,
   *  der naechste Schritt ist sofort faellig. */
  /** Die Person hat geantwortet — macht einen auf Antwort wartenden Schritt sofort fällig.
   *  Kein Abschluss: in dieser Sequenz ist die Antwort die Bedingung fürs Weitermachen. */
  crmAntwort: (leadId: number, personKey: string,
               aktion: 'angenommen' | 'geantwortet' | 'seite_geoeffnet' = 'geantwortet') =>
    apiFetch<{ stand: unknown }>(`${API}/crm/antwort`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey, aktion }),
    }),
  /** Die Person hat um eine Mail gebeten. Hält den Zeitpunkt fest — ohne ihn darf an
   *  Geschäftskunden keine Werbemail raus (§ 7 Abs. 2 Nr. 2 UWG). */
  crmMailErlaubt: (leadId: number, personKey: string) =>
    apiFetch<{ stand: unknown }>(`${API}/crm/mail-erlaubt`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey }),
    }),
  crmSchrittUebersprungen: (leadId: number, personKey: string, grund = 'kein_beitrag') =>
    apiFetch<{ stand: unknown }>(`${API}/crm/schritt-uebersprungen`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey, grund }),
    }),
  crmZustand: (leadId: number, personKey: string, zustand: string, notiz?: string) =>
    apiFetch<{ stand: unknown }>(`${API}/crm/zustand`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey, zustand, notiz }),
    }),
  crmWiedervorlage: (leadId: number, personKey: string, tage: number) =>
    apiFetch<{ stand: unknown }>(`${API}/crm/wiedervorlage`, {
      method: 'POST', body: JSON.stringify({ lead_id: leadId, person_key: personKey, tage }),
    }),
  /** Alle bereits vernetzten Personen in die Standard-Sequenz übernehmen. */
  crmNachziehen: () => apiFetch<{ gestartet: number }>(`${API}/crm/nachziehen`, { method: 'POST' }),

  /** vCard der Arbeitsliste (Bereich + Suche). `nurNeue`: nur Personen, die dieser Nutzer noch
   *  nie heruntergeladen hat. Der Server vermerkt die enthaltenen als exportiert. */
  crmVcard: (bereich: string, suche: string | undefined, nurNeue: boolean) =>
    apiFetch<{ vcf: string; anzahl: number; ohne_nummer: number; schon_exportiert: number }>(
      `${API}/crm/vcard`, {
        method: 'POST',
        body: JSON.stringify({ bereich, suche: suche || null, nur_neue: nurNeue }),
      }),

  /** Wen dieser Nutzer schon als vCard geladen hat (Arbeitsliste baut ihre vCard selbst). */
  crmVcardStand: () =>
    apiFetch<{ personen: { lead_id: number; person_key: string }[] }>(`${API}/crm/vcard/stand`),
  crmVcardMarkiert: (personen: { lead_id: number; person_key: string }[]) =>
    apiFetch<{ markiert: number }>(`${API}/crm/vcard/markiert`, {
      method: 'POST', body: JSON.stringify({ personen }),
    }),

  /** Die persoenliche Seite EINES Leads · `seite: null`, wenn keine angelegt ist. Der
   *  Mail-Composer fragt damit, ob der Link in den Text darf. */
  crmSeite: (leadId: number) =>
    apiFetch<{ seite: CrmSeite | null }>(`${API}/crm/seite/${leadId}`),
  /** Den Eintrag fuer die persoenliche Seite rechnen und merken. Veroeffentlicht wird beim
   *  naechsten Website-Deploy — der Link erscheint erst danach in der Nachricht. */
  crmSeiteAnlegen: (leadId: number, personKey: string, adressat?: 'marketing' | 'gf') =>
    apiFetch<{ seite: CrmSeite }>(`${API}/crm/seite`, {
      method: 'POST',
      body: JSON.stringify({ lead_id: leadId, person_key: personKey, adressat }),
    }),
  crmSeiteLoeschen: (leadId: number) =>
    apiFetch<{ geloescht: boolean }>(`${API}/crm/seite/${leadId}`, { method: 'DELETE' }),
  crmSeitenOffen: () =>
    apiFetch<{ seiten: CrmSeite[]; typescript: string }>(`${API}/crm/seiten`),
  /** Je Schritt: gesendet und wie oft danach eine Antwort kam. */
  crmWirkung: (sequenzId?: number) =>
    apiFetch<{ schritte: { schritt_id: number; schritt_name: string; sequenz_id: number;
                           gesendet: number; geantwortet: number }[] }>(
      `${API}/crm/wirkung${sequenzId ? `?sequenz_id=${sequenzId}` : ''}`),

  // ── Sequenzen ─────────────────────────────────────────────────────────────
  // Jede schreibende Antwort liefert die GANZE Gruppe zurück — die Oberfläche muss nach
  // keinem Speichern nachladen, und Positionen können sich serverseitig verschoben haben.
  listSequenzen: (kanal = 'linkedin') =>
    apiFetch<{ sequenzen: Sequenz[]; kanaele: string[] }>(`${API}/sequenzen?kanal=${encodeURIComponent(kanal)}`),
  getSequenz: (id: number) => apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen/${id}`),
  createSequenz: (body: { name: string; beschreibung?: string; kanal?: string; vorlage?: string }) =>
    apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen`, { method: 'POST', body: JSON.stringify(body) }),
  updateSequenz: (id: number, body: { name?: string; beschreibung?: string; archiviert?: boolean }) =>
    apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSequenz: (id: number) =>
    apiFetch<{ ok: boolean }>(`${API}/sequenzen/${id}`, { method: 'DELETE' }),
  addSchritt: (sequenzId: number, body: { name: string; text?: string; notiz?: string; kanal?: string; wartetage?: number; ueberspring_grund?: string; warten_auf?: string; spaetestens_tage?: number; schleife?: number }) =>
    apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen/${sequenzId}/schritte`,
      { method: 'POST', body: JSON.stringify(body) }),
  updateSchritt: (schrittId: number, body: Partial<{ name: string; text: string; notiz: string; kanal: string; wartetage: number; ueberspring_grund: string; warten_auf: string; spaetestens_tage: number; schleife: number }>) =>
    apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen/schritte/${schrittId}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSchritt: (schrittId: number) =>
    apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen/schritte/${schrittId}`, { method: 'DELETE' }),
  /** Neue Reihenfolge. `ids` muss ALLE Schritte der Gruppe enthalten — eine Teilliste lehnt
   *  der Server ab, statt sie halb auszuführen. */
  sortSchritte: (sequenzId: number, ids: number[]) =>
    apiFetch<{ sequenz: Sequenz }>(`${API}/sequenzen/${sequenzId}/reihenfolge`,
      { method: 'POST', body: JSON.stringify({ ids }) }),

  // LinkedIn FR tracking (observation: per-account ratio, daily-limit warning, event log) --------
  connectionStats: () => apiFetch<ConnectionStats>(`${API}/connection-stats`),
  frTracking: () => apiFetch<FrTracking>(`${API}/fr-tracking`),
  logBlocked: (body: { account_id: number; occurred_at?: string; note?: string }) =>
    apiFetch<{ id: number; ok: boolean }>(`${API}/fr-events/blocked`, {
      method: 'POST', body: JSON.stringify(body),
    }),
};
